import type { ProductionReferenceData, ProductionRun } from "./models";
import { hasVisibleWorkOrder } from "./work-order-display";

const productionIdentity = (run: ProductionRun) => `${run.lineId}\u0000${run.workOrderNo}\u0000${run.productCode}`;

/**
 * A direct terminal start is the operational record for an order. The
 * immutable plan row stays in history, but an untouched source placeholder
 * must not double the live manager target once that exact order is running.
 */
export function withoutUnstartedPlanDuplicates(runs: ProductionRun[]) {
  const startedDirectRuns = new Set(runs
    .filter((run) => !run.planFlowKey && run.disposition !== "CANCELLED" &&
      (run.status === "ACTIVE" || run.status === "COMPLETED") && Boolean(run.startedAt))
    .map(productionIdentity));
  if (startedDirectRuns.size === 0) return runs;
  return runs.filter((run) => {
    if (!run.planFlowKey || !hasVisibleWorkOrder(run) || run.status !== "READY" || !startedDirectRuns.has(productionIdentity(run))) return true;
    const isAutomaticLegacyHold = run.disposition === "ON_HOLD" && run.closeNote === "PLAN_WINDOW_EXPIRED";
    const isOpenPlaceholder = run.disposition === "OPEN";
    return !(isOpenPlaceholder || isAutomaticLegacyHold) || Boolean(run.startedAt ||
      run.planNoteAcknowledgedAt || run.entries.length || run.downtimes.length || run.decisions.length || run.complaints.length);
  });
}

export function runsForShift(runs: ProductionRun[], date: string, shift: string, reference: ProductionReferenceData | null) {
  const clock = shift === "1. Vardiya" ? "00:00" : shift === "2. Vardiya" ? "08:00" : "16:00";
  const start = new Date(`${date}T${clock}:00+03:00`).getTime();
  const end = start + 8 * 3_600_000;
  const projected = runs.flatMap((run) => {
    if (run.planItemKind === "ACTIVITY") return [];
    if (!run.planFlowKey) return run.shiftDate === date && run.shiftCode === shift ? [run] : [];
    const entries = run.entries.filter((entry)=>new Date(entry.occurredAt).getTime()>=start && new Date(entry.occurredAt).getTime()<end);
    const overlaps = Boolean(run.startedAt && new Date(run.startedAt).getTime()<end && (!run.closedAt || new Date(run.closedAt).getTime()>start));
    const scheduleDate = date;
    const allocations=reference?.scheduleItems.filter((item)=>item.lineId===run.lineId && item.sequence===run.sequenceNo && item.date===scheduleDate && item.shiftCode===shift) ?? [];
    if (!entries.length && !overlaps && !allocations.length) return [];
    const downtimes = run.downtimes.filter((event) => Date.parse(event.startedAt) < end && (!event.endedAt || Date.parse(event.endedAt) > start));
    return [{...run, entries,downtimes,reportWindow:{start,end},shiftDate:date,shiftCode:shift,
      plannedQuantity:allocations.length ? allocations.reduce((sum,item)=>sum+item.quantity,0) : null,
      startedAt:run.startedAt ? new Date(Math.max(start,new Date(run.startedAt).getTime())).toISOString() : null,
      closedAt:run.closedAt ? new Date(Math.min(end,new Date(run.closedAt).getTime())).toISOString() : Date.now()>=end ? new Date(end).toISOString() : null,
    }];
  });
  return withoutUnstartedPlanDuplicates(projected);
}
